import React, { Component } from "react";
import {
  Button,
  MenuItem,
  Switch,
  Menu,
  Popover,
  Position,
  Tag
} from "@blueprintjs/core";
import { observable, action } from "mobx";
import { observer, inject } from "mobx-react";
import ListPicker from "components/ListPicker";
import SearchDateRangePicker from "components/SearchDateRangePicker";
import ClientAutocomplete from "components/ClientAutocomplete";
import TechnicianPicker from "components/TechnicianPicker";
import ServiceTypePicker from "components/ServiceTypePicker";
import PriorityTagPicker from "components/PriorityTagPicker";
import moment from "moment-timezone";
import { formatDateRange } from 'helpers/dateRange';
import SearchFiltersToolbar from "components/SearchFiltersToolbar";
import SearchFiltersSearchButton from "components/SearchFiltersSearchButton";
import DispatchViewBar from 'components/DispatchViewBar';
import { jobStatusOptions } from "helpers/jobStatusOptions";

const options = jobStatusOptions();

// Payment pills match the Billing column in the jobs list.
const payment_options = [
  { label: "Invoiced", value: "invoiced", selected: false, pill: { background: "#e6f4ea", text: "#1e6b3a" } },
  { label: "Not Invoiced", value: "not_invoiced", selected: false, pill: { background: "#f3f5fa", text: "#4f5d72" } },
];

@inject("store", "jobStore", "viewStore")
@observer
class Job extends Component {
  @observable selectedClient = "";

  @action
  onAssignmentStatusChange(selections) {
    let selectedValues = selections.map((status) => status.value);
    let {
      props: {
        viewStore: { jobSearch }
      }
    } = this;
    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        assignment_status: selectedValues
      }
    };
  }

  onPriorityChange(selections) {
    const selectedValues = selections
      .map((option) => option.value)
      .filter((value) => value !== "selectAll");
    const {
      props: {
        viewStore: {
          jobSearch
        }
      }
    } = this;
    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        priority: selectedValues
      }
    }
  }

  onPaymentStatusChange(selections) {
    let selectedValues = selections.map((status) => status.value);
    let {
      props: {
        viewStore: {
          jobSearch
        }
      }
    } = this;
    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        payment_status: selectedValues
      }
    }
  }

  @action
  onDateRangeChange(range) {
    let {
      props: {
        viewStore: { jobSearch }
      }
    } = this;
    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        start_date: {
          gte: moment(range[0]).startOf("day").unix()
        },
        end_date: {
          lte: moment(range[1]).endOf("day").unix()
        }
      }
    };
    this.startDate = range[0];
    this.endDate = range[1];
    this.search();
  }

  onTechnicianChange(technicians) {
    let selectedValues = technicians.map((status) => status.value);
    let {
      props: {
        viewStore: { jobSearch }
      }
    } = this;
    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        user_id: selectedValues
      }
    };
  }

  onServiceTypeChange(selections) {
    let selectedValues = selections.map((service_type) => service_type.value);
    let {
      props: {
        viewStore: { jobSearch }
      }
    } = this;
    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        service_type_id: selectedValues
      }
    };
  }

  @action
  onClientSelect(client) {
    let {
      props: {
        viewStore: {
          jobSearch,
          jobSearch: {
            where: { client_id, ...whereWithNoClientId }
          }
        }
      }
    } = this;
    if (client) {
      this.props.viewStore.jobSearch = {
        ...jobSearch,
        where: {
          ...jobSearch.where,
          client_id: client.id
        }
      };
    } else {
      this.props.viewStore.jobSearch = {
        ...jobSearch,
        where: {
          ...whereWithNoClientId
        }
      };
    }
  }

  @action
  onShowDeletedChange(e) {
    let {
      props: {
        viewStore: { jobSearch }
      }
    } = this;
    this.props.viewStore.jobSearch = {
      ...jobSearch,
      only_deleted: e.target.checked
    };
    this.search();
  }

  @action
  onJobNumberChange(e) {
    let {
      props: {
        viewStore: { jobSearch }
      }
    } = this;
    let eventNumber;
    if (e.target.value === "") {
      eventNumber = null;
    } else {
      eventNumber = e.target.value;
    }

    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        event_number: eventNumber
      }
    };
  }

  @action
  onPONumberChange(e) {
    let {
      props: {
        viewStore: { jobSearch }
      }
    } = this;
    let poNumber;
    if (e.target.value === "") {
      poNumber = null;
    } else {
      poNumber = e.target.value;
    }

    this.props.viewStore.jobSearch = {
      ...jobSearch,
      where: {
        ...jobSearch.where,
        po_number: poNumber
      }
    };
  }

  onEnter(event) {
    if (event.key === "Enter") {
      this.search();
    }
  }

  @action
  search() {
    let viewStore = this.props.viewStore;
    viewStore.jobSearch.page = 1;
    viewStore.jobs();
  }

  changeView(view) {
    let { viewStore } = this.props;
    viewStore.dashboardSearch.view = view;
    viewStore.dashboard();
  }

  download_excel() {
    let { viewStore, jobStore } = this.props;
    jobStore.download_excel(viewStore.jobSearch);
  }

  render() {
    const {
      viewStore: {
        jobSearch,
        jobSearch: { where }
      }
    } = this.props;
    let startDate = moment.unix(parseInt(where.start_date.gte)).toDate();
    let endDate = moment.unix(parseInt(where.end_date.lte)).toDate();
    return (
      <SearchFiltersToolbar
        header={<DispatchViewBar current="jobs" />}
        searchButton={
          <SearchFiltersSearchButton onClick={this.search.bind(this)} />
        }
        actions={
          <Popover
            minimal={true}
            content={
              <Menu>
                <MenuItem
                  text="Excel/CSV"
                  onClick={this.download_excel.bind(this)}
                />
              </Menu>
            }
            position={Position.BOTTOM_LEFT}
            usePortal={true}
          >
            <Button rightIcon="caret-down">Download</Button>
          </Popover>
        }
      >
        <Popover
          minimal={true}
          content={
            <SearchDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={this.onDateRangeChange.bind(this)}
            />
          }
          position={Position.BOTTOM_LEFT}
          usePortal={true}
        >
          <Button
            className="filter-date"
            icon="calendar"
            rightIcon="caret-down"
            text={formatDateRange(startDate, endDate)}
          />
        </Popover>

        <ClientAutocomplete
          selectedClientId={where && where.client_id}
          onClientSelect={this.onClientSelect.bind(this)}
        />

        <TechnicianPicker
          selectedOptions={(where && where.user_id) || []}
          onChange={this.onTechnicianChange.bind(this)}
          includeAdmins={true}
        />

        <Popover
          minimal={true}
          content={
            <ListPicker
              selectedOptions={(where && where.assignment_status) || []}
              options={options}
              onChange={this.onAssignmentStatusChange.bind(this)}
            />
          }
          position={Position.BOTTOM_LEFT}
          usePortal={true}
        >
          <Button rightIcon="caret-down">
            Status
            <span>&nbsp;</span>
            <Tag minimal={true} round={true}>
              {(where &&
                where.assignment_status &&
                where.assignment_status.length) ||
                0}
            </Tag>
          </Button>
        </Popover>

        <Popover
          minimal={true}
          content={
            <ListPicker
              selectedOptions={(where && where.payment_status) || []}
              options={payment_options}
              onChange={this.onPaymentStatusChange.bind(this)}
            />
          }
          position={Position.BOTTOM_LEFT}
          usePortal={true} >
          <Button rightIcon='caret-down'>
            Payment Status
            <span>&nbsp;</span>
            <Tag minimal={true} round={true}>{(where && where.payment_status && where.payment_status.length) || 0}</Tag>
          </Button>
        </Popover>

        <PriorityTagPicker
          selectedOptions={(where && where.priority) || []}
          onChange={this.onPriorityChange.bind(this)}
          includeSelectAll={true}
        />

        <ServiceTypePicker
          selectedOptions={(where && where.service_type_id) || ''}
          onChange={this.onServiceTypeChange.bind(this)}
        />

        <input
          type="text"
          className="bp3-input"
          placeholder="Job Number"
          onChange={this.onJobNumberChange.bind(this)}
          defaultValue={where.event_number}
          onKeyPress={this.onEnter.bind(this)}
        />

        <input
          type="text"
          className="bp3-input"
          placeholder="PO Number"
          onChange={this.onPONumberChange.bind(this)}
          defaultValue={where.po_number}
          onKeyPress={this.onEnter.bind(this)}
        />

        <div className="job-deleted-wrapper">
          <Switch
            checked={jobSearch.only_deleted}
            label="Show Deleted"
            onChange={this.onShowDeletedChange.bind(this)}
          />
        </div>
      </SearchFiltersToolbar>
    );
  }
}

export default Job;
